/**
 * Dark vertical step sidebar for the self-service "Create workspace" wizard.
 *
 * Renders the five onboarding steps with completed / active / upcoming
 * states connected by vertical lines, over the contour-map texture from the
 * design. Completed steps link back to their page; the active and upcoming
 * steps are not clickable.
 */
import { Link } from 'react-router-dom';

import {
  SELF_SERVICE_STEPS,
  selfServiceStepPath,
} from '@/lib/self-service/constants';
import { cx } from '@/utils/cx';

interface StepRowProps {
  index: number;
  label: string;
  stepId: string;
  currentIndex: number;
  isLast: boolean;
  stepPath: (stepId: string) => string;
}

function StepRow({
  index,
  label,
  stepId,
  currentIndex,
  isLast,
  stepPath,
}: StepRowProps) {
  const isActive = index === currentIndex;
  const isCompleted = index < currentIndex;

  const row = (
    <div
      className='flex items-center gap-4'
      aria-current={isActive ? 'step' : undefined}
    >
      <span
        className={cx(
          'flex size-7 shrink-0 items-center justify-center rounded-sm font-mono text-sm',
          isActive && 'bg-white font-semibold text-brand-900',
          isCompleted && 'bg-white/70 text-brand-900',
          !isActive && !isCompleted && 'bg-white/10 text-white/60'
        )}
      >
        {index + 1}
      </span>
      <span
        className={cx(
          'font-mono text-sm tracking-wide',
          isActive && 'text-white',
          isCompleted && 'text-white/70 group-hover:text-white',
          !isActive && !isCompleted && 'text-white/60'
        )}
      >
        {label}
      </span>
    </div>
  );

  return (
    <li>
      {isCompleted ? (
        <Link to={stepPath(stepId)} className='group block'>
          {row}
        </Link>
      ) : (
        row
      )}
      {!isLast && <div aria-hidden className='ml-3.5 h-8 w-px bg-white/20' />}
    </li>
  );
}

interface StepSidebarProps {
  currentIndex: number;
  /** Steps to render. Defaults to the self-service wizard's step registry. */
  steps?: readonly { id: string; label: string }[];
  /** Resolves a step's href. Defaults to the self-service step path helper. */
  stepPath?: (stepId: string) => string;
}

export function StepSidebar({
  currentIndex,
  steps,
  stepPath,
}: StepSidebarProps) {
  const resolvedSteps = steps ?? SELF_SERVICE_STEPS;
  const resolvedStepPath = stepPath ?? selfServiceStepPath;

  return (
    <nav
      aria-label='Setup steps'
      className="hidden w-[360px] shrink-0 bg-brand-900 bg-[url('/assets/self-service/img/sidebar-texture.png')] bg-cover bg-center lg:block"
    >
      <ol className='flex flex-col px-10 pt-24'>
        {resolvedSteps.map((step, index) => (
          <StepRow
            key={step.id}
            index={index}
            label={step.label}
            stepId={step.id}
            currentIndex={currentIndex}
            isLast={index === resolvedSteps.length - 1}
            stepPath={resolvedStepPath}
          />
        ))}
      </ol>
    </nav>
  );
}
